import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table.tsx";
import { Content } from "@/components/layouts/content";
import { caseActivitySettingsQueryOptions, useToggleEditFiledAt } from "@/features/settings/api/queries/case-activity-settings-query";
import type { CaseActivitySetting } from "@/features/settings/api/types/case-activity-settings";
import { formatDate } from "@/utils/format-date";
import { IconLock, IconLockOpen } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import clsx from "clsx";

export const Route = createFileRoute("/settings/case-activity-settings")({
	loader: ({ context: { queryClient } }) => {
		return queryClient.ensureQueryData(caseActivitySettingsQueryOptions());
	},
	component: CaseActivitySettingsPage,
});

function CaseActivitySettingsPage() {
	const { data: settings } = useSuspenseQuery(caseActivitySettingsQueryOptions());
	const toggleMutation = useToggleEditFiledAt();

	const handleToggle = () => {
		toggleMutation.mutate();
	};

	const columns: ColumnDef<CaseActivitySetting>[] = [
		{
			id: "setting",
			label: "Setting",
			cell: ({ data }) => (
				<div className="flex items-center gap-3">
					<div>
						<span className="font-bold text-foreground-dark">Filed/Ordered At Date Editing</span>
						<div className="text-sm text-foreground/70">Control whether users can modify the filed at date for case activities</div>
					</div>
				</div>
			),
		},
		{
			id: "status",
			label: "Status",
			cell: ({ data }) => (
				<span
					className={clsx(
						"inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize",
						data.allow_edit_filed_at ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800",
					)}
				>
					{data.status}
				</span>
			),
		},
		{
			id: "updated_at",
			label: "Last Updated",
			cell: ({ data }) => formatDate(data.updated_at || "-"),
		},
		{
			id: "actions",
			label: "Actions",
			cell: ({ data }) => (
				<Button onClick={handleToggle} disabled={toggleMutation.isPending} variant={data.allow_edit_filed_at ? "destructive" : "primary"} size="sm">
					{toggleMutation.isPending ? (
						"Updating..."
					) : data.allow_edit_filed_at ? (
						<>
							<IconLock size={16} stroke={2} className="mr-1" />
							Disable
						</>
					) : (
						<>
							<IconLockOpen size={16} stroke={2} className="mr-1" />
							Enable
						</>
					)}
				</Button>
			),
		},
	];

	// Convert single settings object to array for DataTable
	const settingsData = [settings];

	return (
		<Content pageName="Case Activity Settings">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Case Activity Settings</span>
						<span className="text-base text-foreground/70">Configure permissions and behaviors for case activities</span>
					</div>
				</div>
				<div className="flex flex-col w-full gap-4">
					<DataTable showToolbar={false} data={settingsData} columns={columns} showBorder={true} compact={true} />
				</div>
			</div>
		</Content>
	);
}
